import Button from "@/components/Button";
import AddItemForm from "@/pages/Admin/components/OrgManagement/AddItemForm";
import OrgItem from "@/pages/Admin/components/OrgManagement/OrgItem";
import {
  CAMPUS_CD,
  MobileTab,
  OrgActionsState,
  OrgTreeState,
} from "@/pages/Admin/components/OrgManagement/types";

// ---- 공동체/다락방/순 3단 컬럼 패널 ----

interface OrgTreePanelProps {
  tree: OrgTreeState;
  actions: OrgActionsState;
  deleteMode: boolean;
  mobileTab: MobileTab;
}

const OrgTreePanel = ({
  tree: {
    communities,
    garrets,
    leafs,
    selectedCmtCd,
    setSelectedCmtCd,
    selectedGarCd,
    setSelectedGarCd,
    selectedLeafCd,
    setSelectedLeafCd,
  },
  actions: {
    showAddCmt,
    setShowAddCmt,
    showAddGar,
    setShowAddGar,
    showAddLeaf,
    setShowAddLeaf,
    editCmtCd,
    setEditCmtCd,
    editCmtNm,
    setEditCmtNm,
    editGarCd,
    setEditGarCd,
    editGarNm,
    setEditGarNm,
    editLeafCd,
    setEditLeafCd,
    editLeafNm,
    setEditLeafNm,
    clearAllEdits,
    openUserList,
    handleAddCommunity,
    handleSaveCommunity,
    handleRequestDeleteCommunity,
    handleAddGarret,
    handleSaveGarret,
    handleRequestDeleteGarret,
    handleAddLeaf,
    handleSaveLeaf,
    handleRequestDeleteLeaf,
  },
  deleteMode,
  mobileTab,
}: OrgTreePanelProps) => {
  const colHeaderCls =
    "flex h-11 shrink-0 items-center justify-between border-b border-gray-200 bg-gray-50 px-3";

  return (
    <div className="flex gap-0 overflow-hidden rounded-lg border border-gray-200 bg-white">
      {/* 공동체 컬럼 */}
      <div
        className={`flex-col overflow-hidden border-r border-gray-200 ${mobileTab === "cmt" ? "flex w-full" : "hidden"} md:flex md:w-1/3`}
      >
        <div className={colHeaderCls}>
          <span className="text-small font-medium text-gray-dark">공동체</span>
          <Button
            variant="add"
            className="py-0.5 text-small"
            onClick={() => setShowAddCmt(v => !v)}
          >
            추가
          </Button>
        </div>

        {showAddCmt && (
          <AddItemForm
            fields={[{ key: "cmtNm", placeholder: "공동체명" }]}
            onAdd={handleAddCommunity}
            onCancel={() => setShowAddCmt(false)}
          />
        )}

        <div className="flex h-[360px] flex-col overflow-y-auto">
          {communities.length === 0 ? (
            <p className="p-3 text-small text-gray-dull">공동체 없음</p>
          ) : (
            communities.map(c => (
              <OrgItem
                key={c.cmtCd}
                name={c.cmtNm}
                isSelected={selectedCmtCd === c.cmtCd}
                isEditing={editCmtCd === c.cmtCd}
                editValue={editCmtNm}
                onSelect={() => {
                  clearAllEdits();
                  setSelectedCmtCd(c.cmtCd);
                }}
                onEditChange={setEditCmtNm}
                onEditSave={handleSaveCommunity}
                onEditCancel={() => setEditCmtCd(null)}
                onEditStart={() => {
                  setEditCmtCd(c.cmtCd);
                  setEditCmtNm(c.cmtNm);
                }}
                onDeleteRequest={() => handleRequestDeleteCommunity(c)}
                deleteMode={deleteMode}
                onClickCount={() =>
                  openUserList({
                    cpsCd: CAMPUS_CD,
                    cmtCd: c.cmtCd,
                    name: c.cmtNm,
                  })
                }
              />
            ))
          )}
        </div>
      </div>

      {/* 다락방 컬럼 */}
      <div
        className={`flex-col overflow-hidden border-r border-gray-200 ${mobileTab === "gar" ? "flex w-full" : "hidden"} md:flex md:w-1/3`}
      >
        <div className={colHeaderCls}>
          <span className="text-small font-medium text-gray-dark">다락방</span>
          {selectedCmtCd && (
            <Button
              variant="add"
              className="py-0.5 text-small"
              onClick={() => setShowAddGar(v => !v)}
            >
              추가
            </Button>
          )}
        </div>

        {showAddGar && selectedCmtCd && (
          <AddItemForm
            fields={[{ key: "garNm", placeholder: "다락방명" }]}
            onAdd={handleAddGarret}
            onCancel={() => setShowAddGar(false)}
          />
        )}

        <div className="flex h-[360px] flex-col overflow-y-auto">
          {!selectedCmtCd ? (
            <p className="p-3 text-small text-gray-dull">
              공동체를 선택하세요.
            </p>
          ) : garrets.length === 0 ? (
            <p className="p-3 text-small text-gray-dull">다락방 없음</p>
          ) : (
            garrets.map(g => (
              <OrgItem
                key={g.garCd}
                name={g.garNm}
                isSelected={selectedGarCd === g.garCd}
                isEditing={editGarCd === g.garCd}
                editValue={editGarNm}
                onSelect={() => {
                  clearAllEdits();
                  setSelectedGarCd(g.garCd);
                }}
                onEditChange={setEditGarNm}
                onEditSave={handleSaveGarret}
                onEditCancel={() => setEditGarCd(null)}
                onEditStart={() => {
                  setEditGarCd(g.garCd);
                  setEditGarNm(g.garNm);
                }}
                onDeleteRequest={() => handleRequestDeleteGarret(g)}
                deleteMode={deleteMode}
                onClickCount={() =>
                  openUserList({
                    cpsCd: CAMPUS_CD,
                    cmtCd: g.cmtCd,
                    garCd: g.garCd,
                    name: g.garNm,
                  })
                }
              />
            ))
          )}
        </div>
      </div>

      {/* 순 컬럼 */}
      <div
        className={`flex-col overflow-hidden ${mobileTab === "leaf" ? "flex w-full" : "hidden"} md:flex md:w-1/3`}
      >
        <div className={colHeaderCls}>
          <span className="text-small font-medium text-gray-dark">순</span>
          {selectedGarCd && (
            <Button
              variant="add"
              className="py-0.5 text-small"
              onClick={() => setShowAddLeaf(v => !v)}
            >
              추가
            </Button>
          )}
        </div>

        {showAddLeaf && selectedGarCd && (
          <AddItemForm
            fields={[{ key: "leafNm", placeholder: "순 이름" }]}
            onAdd={handleAddLeaf}
            onCancel={() => setShowAddLeaf(false)}
          />
        )}

        <div className="flex h-[360px] flex-col overflow-y-auto">
          {!selectedGarCd ? (
            <p className="p-3 text-small text-gray-dull">
              다락방을 선택하세요.
            </p>
          ) : leafs.length === 0 ? (
            <p className="p-3 text-small text-gray-dull">순 없음</p>
          ) : (
            leafs.map(l => (
              <OrgItem
                key={l.leafCd}
                name={l.leafNm}
                isSelected={selectedLeafCd === l.leafCd}
                isEditing={editLeafCd === l.leafCd}
                editValue={editLeafNm}
                onSelect={() => {
                  clearAllEdits();
                  setSelectedLeafCd(l.leafCd);
                }}
                onEditChange={setEditLeafNm}
                onEditSave={handleSaveLeaf}
                onEditCancel={() => setEditLeafCd(null)}
                onEditStart={() => {
                  setEditLeafCd(l.leafCd);
                  setEditLeafNm(l.leafNm);
                }}
                onDeleteRequest={() => handleRequestDeleteLeaf(l)}
                deleteMode={deleteMode}
                onViewMembers={() =>
                  openUserList({
                    cpsCd: CAMPUS_CD,
                    cmtCd: l.cmtCd,
                    garCd: l.garCd,
                    leafCd: l.leafCd,
                    name: l.leafNm,
                  })
                }
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default OrgTreePanel;
